import { NotificationEvent, NotificationMember } from '../gql/generated';
import { formatMetricName, formatThreshold } from './formatMetric';

/**
 * The alarms one notification covers. A send for an alert group covers its
 * member alarms. Every other send covers the alarm on the row itself.
 */
export function notificationMembers(
  notification: NotificationEvent
): NotificationMember[] {
  if (notification.members && notification.members.length > 0) {
    return notification.members;
  }

  return [
    {
      alarmId: notification.alarmId,
      alarmName: notification.alarmName,
      signal: notification.signal,
      unit: notification.unit,
      signalCategory: notification.signalCategory,
      thresholdMode: notification.thresholdMode,
      thresholdLow: notification.thresholdLow,
      thresholdHigh: notification.thresholdHigh,
      triggerValues: notification.triggerValues,
    },
  ];
}

/** The value every member shares, or how many values they hold. */
function sharedOrCount(
  values: Array<string | null | undefined>,
  plural: string
): string | null {
  const distinct = [...new Set(values.filter(Boolean) as string[])];

  if (distinct.length === 0) return null;
  if (distinct.length === 1) return distinct[0];
  return `${distinct.length} ${plural}`;
}

/** The signal of one send: `Enclosure Temperature`, or `4 signals`. */
export function notificationSignalLabel(
  notification: NotificationEvent
): string | null {
  return sharedOrCount(
    notificationMembers(notification).map((member) =>
      member.signal ? formatMetricName(member.signal) : null
    ),
    'signals'
  );
}

/** The alert of one send: `Cold room`, or `4 alerts`. */
export function notificationAlarmLabel(
  notification: NotificationEvent
): string | null {
  return sharedOrCount(
    notificationMembers(notification).map((member) => member.alarmName),
    'alerts'
  );
}

/** The threshold of one send: `Above 8 °C`, or `4 thresholds`. */
export function notificationThresholdLabel(
  notification: NotificationEvent
): string | null {
  return sharedOrCount(
    notificationMembers(notification).map((member) =>
      member.thresholdMode
        ? formatThreshold({
            thresholdMode: member.thresholdMode,
            thresholdLow: member.thresholdLow ?? 0,
            thresholdHigh: member.thresholdHigh ?? 0,
            unit: member.unit,
            signal: member.signal,
            signalCategory: member.signalCategory,
          })
        : null
    ),
    'thresholds'
  );
}
